import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, User } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/directory", label: "Eat & Drink" },
  { to: "/articles", label: "Editorial" },
  { to: "/events", label: "Events" },
  { to: "/watch", label: "Watch" },
  { to: "/services", label: "Business Services" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();

  return (
    <header
      data-testid="site-navbar"
      className="fixed top-0 inset-x-0 z-50 bg-sand-50/85 backdrop-blur-xl border-b border-sand-200"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo-link" className="flex items-baseline gap-2">
          <span className="font-serif font-black text-2xl md:text-3xl tracking-tight text-sand-900">
            Tastebuds
          </span>
          <span className="hidden sm:inline font-mono text-[10px] tracking-[0.25em] uppercase text-brand-500">
            Food Network
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-link-${n.label.toLowerCase().replace(/\s|&/g, "-")}`}
              className={({ isActive }) =>
                `font-sans text-[11px] font-bold tracking-[0.2em] uppercase transition-colors ${
                  isActive ? "text-brand-500" : "text-sand-900 hover:text-brand-500"
                }`
              }
              end={n.to === "/"}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-5">
          {user ? (
            <Link
              to="/dashboard"
              data-testid="nav-dashboard-link"
              className="flex items-center gap-2 font-mono text-[11px] font-bold tracking-[0.2em] uppercase text-sand-900 hover:text-brand-500 transition-colors"
            >
              <User size={15} /> Dashboard
            </Link>
          ) : (
            <Link
              to="/login"
              data-testid="nav-login-link"
              className="font-mono text-[11px] font-bold tracking-[0.2em] uppercase text-sand-900 hover:text-brand-500 transition-colors"
            >
              Sign In
            </Link>
          )}
          {user?.role === "admin" && (
            <Link
              to="/admin"
              data-testid="nav-admin-link"
              className="font-mono text-[11px] font-bold tracking-[0.2em] uppercase text-brand-500 hover:text-brand-600 transition-colors"
            >
              Admin
            </Link>
          )}
          <Link
            to="/services"
            data-testid="nav-cta-pitch"
            className="bg-sand-900 text-sand-50 px-5 py-3 text-[11px] font-bold tracking-[0.2em] uppercase hover:bg-brand-500 transition-colors"
          >
            Pitch Your Brand
          </Link>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden p-2 text-sand-900"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-sand-50 border-t border-sand-200" data-testid="nav-mobile-panel">
          <div className="px-6 py-6 flex flex-col gap-5">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-mobile-link-${n.label.toLowerCase().replace(/\s|&/g, "-")}`}
                className="font-sans text-sm font-bold tracking-[0.2em] uppercase text-sand-900"
                onClick={() => setOpen(false)}
              >
                {n.label}
              </Link>
            ))}
            <Link
              to={user ? "/dashboard" : "/login"}
              data-testid="nav-mobile-auth"
              onClick={() => setOpen(false)}
              className="font-sans text-sm font-bold tracking-[0.2em] uppercase text-brand-500"
            >
              {user ? "Dashboard" : "Sign In"}
            </Link>
            <Link
              to="/services"
              data-testid="nav-mobile-cta"
              onClick={() => setOpen(false)}
              className="bg-sand-900 text-sand-50 px-5 py-3 text-xs font-bold tracking-[0.2em] uppercase text-center"
            >
              Pitch Your Brand
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
